import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import FormInput from '../../components/elements/form_input';
import Button from '../../components/elements/button';
import { Password, Security } from '../../assets/icons';
import { authApi } from '../../network/apis/auth';

const ResetPassword = () => {
    const [step, setStep] = useState(1); // 1: Enter OTP, 2: Enter new password
    const [otp, setOtp] = useState(['', '', '', '', '', '']);
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [email, setEmail] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const navigate = useNavigate();
    const inputRefs = useRef([]);

    useEffect(() => {
        // Get email from session storage (set in forgot password page)
        const storedEmail = sessionStorage.getItem('resetEmail');
        if (storedEmail) {
            setEmail(storedEmail);
        } else {
            // Redirect back to forgot password if no email
            navigate('/forgot-password');
        }
    }, [navigate]);

    // Handle OTP input change
    const handleOtpChange = (index, value) => {
        // Only allow digits
        if (value && !/^\d$/.test(value)) return;

        const newOtp = [...otp];
        newOtp[index] = value;
        setOtp(newOtp);
        setError('');

        // Auto-focus next input
        if (value && index < 5) {
            inputRefs.current[index + 1]?.focus();
        }
    };

    // Handle backspace
    const handleKeyDown = (index, e) => {
        if (e.key === 'Backspace' && !otp[index] && index > 0) {
            inputRefs.current[index - 1]?.focus();
        }
    };

    // Handle paste
    const handlePaste = (e) => {
        e.preventDefault();
        const pastedData = e.clipboardData.getData('text').slice(0, 6);
        if (/^\d+$/.test(pastedData)) {
            const newOtp = pastedData.split('').concat(Array(6 - pastedData.length).fill(''));
            setOtp(newOtp);
            // Focus the next empty input or the last one
            const nextEmptyIndex = newOtp.findIndex(digit => !digit);
            inputRefs.current[nextEmptyIndex !== -1 ? nextEmptyIndex : 5]?.focus();
        }
    };

    const handleVerifyCode = async (e) => {
        e.preventDefault();
        const resetCode = otp.join('');
        
        if (resetCode.length !== 6) {
            setError('Please enter all 6 digits');
            return;
        }

        setIsLoading(true);
        setError('');

        try {
            const response = await authApi.verifyResetCode(email, resetCode);
            if (response?.status === 'success') {
                setStep(2);
            } else {
                setError(response?.message || 'Invalid verification code');
            }
        } catch (error) {
            console.error('Error:', error);
            setError(error.response.data?.message || 'Invalid or expired verification code');
        } finally {
            setIsLoading(false);
        }
    };

    const handleResetPassword = async (e) => {
        e.preventDefault();
        setError('');

        // Validate password
        if (password.length < 8) {
            setError('Password must be at least 8 characters long');
            return;
        }
        
        if (password !== confirmPassword) {
            setError("Passwords don't match!");
            return;
        }

        setIsLoading(true);

        try {
            const resetCode = otp.join('');
            const response = await authApi.resetPassword(email, resetCode, password);
            
            if (response?.status === 'success') {
                // Clear stored email
                sessionStorage.removeItem('resetEmail');
                // Navigate to success page or login
                navigate('/login', { 
                    state: { message: 'Password reset successful! Please login with your new password.' }
                });
            } else {
                setError(response?.message || 'Failed to reset password');
            }
        } catch (error) {
            console.error('Error:', error);
            setError(error.response.data?.message || 'Failed to reset password. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleResendCode = async () => {
        setIsLoading(true);
        setError('');
        try {
            await authApi.resendResetCode(email);
            setOtp(['', '', '', '', '', '']);
            alert('A new reset code has been sent to your email.');
        } catch (error) {
            setError('Failed to resend code. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="auth-container">
            <section className="auth-section">
                <div className="auth-logo-section">
                    E-AQUAHEALTH
                    <img src={Security} alt="security"/>
                </div>
                
                {step === 1 ? (
                    <>
                        <h3 className="auth-title">Verify Reset Code</h3>
                        <p className="auth-subtitle">
                            Enter the 6-digit code sent to <strong>{email}</strong>
                        </p>

                        {error && <div className="auth-error">{error}</div>}

                        <form onSubmit={handleVerifyCode} className="auth-form">
                            <div className="otp-container">
                                {otp.map((digit, index) => (
                                    <input
                                        key={index}
                                        ref={el => inputRefs.current[index] = el}
                                        type="text"
                                        inputMode="numeric"
                                        maxLength={1}
                                        value={digit}
                                        onChange={(e) => handleOtpChange(index, e.target.value)}
                                        onKeyDown={(e) => handleKeyDown(index, e)}
                                        onPaste={handlePaste}
                                        className="otp-input"
                                        autoFocus={index === 0}
                                    />
                                ))}
                            </div>
                            
                            <p className="code-expiry">Code expires in 15 minutes</p>
                            
                            <Button 
                                text={isLoading ? "Verifying..." : "Verify Code"} 
                                type="submit"
                                fullWidth
                                loading={isLoading}
                                disabled={isLoading || otp.join('').length !== 6}
                            />
                            
                            <button 
                                type="button"
                                className="resend-link"
                                onClick={handleResendCode}
                                disabled={isLoading}
                            >
                                {isLoading ? 'Sending...' : "Didn't receive the code? Resend"}
                            </button>
                            
                            <div className="back-to-login">
                                <Link to="/login">← Back to Login</Link>
                            </div>
                        </form>
                    </>
                ) : (
                    <>
                        <h3 className="auth-title">Reset Password</h3>
                        <p className="auth-subtitle">Enter your new password</p>

                        {error && <div className="auth-error">{error}</div>}

                        <form onSubmit={handleResetPassword} className="auth-form">
                            <FormInput
                                id="password"
                                label="New Password"
                                required={true}
                                icon={<img src={Password} alt="password"/>}
                                placeholder="Enter new password (min 8 characters)"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                type="password"
                            />
                            <FormInput
                                id="confirmPassword"
                                label="Confirm Password"
                                required={true}
                                icon={<img src={Password} alt="confirm password"/>}
                                placeholder="Confirm new password"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                type="password"
                            />
                            
                            <div className="password-requirements">
                                <p className={password.length >= 8 ? 'met' : ''}>
                                    • At least 8 characters
                                </p>
                                <p className={password === confirmPassword && password ? 'met' : ''}>
                                    • Passwords match
                                </p>
                            </div>
                            
                            <Button 
                                text={isLoading ? "Resetting..." : "Reset Password"} 
                                type="submit"
                                fullWidth
                                loading={isLoading}
                                disabled={isLoading || password.length < 8 || password !== confirmPassword}
                            />
                            
                            <div className="back-to-login">
                                <Link to="/login">← Back to Login</Link>
                            </div>
                        </form>
                    </>
                )}
            </section>
        </div>
    );
};

export default ResetPassword;